import React from "react";
import NavTop from "../../component/NavTop";
import Modal from "../../component/Modal";
import { toast } from "react-toastify";
import {
  IoIosPaperPlane,
  IoIosLink,
  IoIosTrash,
  IoIosCheckmark,
  IoIosAdd,
} from "react-icons/io";
import { FormatNumber, FormatDate } from "../../helpers";
import {
  usePartyDetail,
  partyShareRegenerate,
  partyShareSend,
  partyShareRevoke,
} from "../../api";
import "./debt.css";
import "./party.css";

const initials = (name) =>
  (name || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const PartyDetail = (props) => {
  const partyId = props.match.params.partyId;
  const { party, isLoading, refresh } = usePartyDetail(partyId);

  const [shareModal, setShareModal] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [confirmRevoke, setConfirmRevoke] = React.useState(false);

  if (isLoading) {
    return (
      <>
        <NavTop title="Counterparty" />
        <div className="main-padding text-center text-muted">Memuat…</div>
      </>
    );
  }
  if (!party) {
    return (
      <>
        <NavTop title="Counterparty" />
        <div className="main-padding text-center text-danger">
          Tidak ditemukan
        </div>
      </>
    );
  }

  const net = party.totals.netOutstanding;
  const direction =
    net > 0
      ? `${party.name} berutang kepada Anda`
      : net < 0
      ? `Anda berutang kepada ${party.name}`
      : "Saldo sudah lunas";
  const netClass = net > 0 ? "owed" : net < 0 ? "you-owe" : "even";

  const handleGenerate = async () => {
    try {
      await partyShareRegenerate(partyId);
      toast.success("Link bagikan siap");
      refresh();
    } catch (err) {}
  };

  const handleSendWa = async () => {
    setSending(true);
    try {
      await partyShareSend(partyId);
      toast.success(`Ringkasan terkirim ke ${party.name} via WhatsApp`);
      setShareModal(false);
      refresh();
    } catch (err) {}
    setSending(false);
  };

  const handleCopy = async () => {
    if (!party.share || !party.share.url) {
      await handleGenerate();
      return;
    }
    try {
      await navigator.clipboard.writeText(party.share.url);
      toast.success("Link disalin");
    } catch (e) {
      toast.error("Salin manual: " + party.share.url);
    }
  };

  const handleRevoke = async () => {
    try {
      await partyShareRevoke(partyId);
      toast.success("Link dicabut");
      setConfirmRevoke(false);
      refresh();
    } catch (err) {}
  };

  return (
    <div className="pty-page">
      <NavTop title={party.name} />
      <div className="main-padding pty-page__inner">
        <div className="pty-hero">
          <div className="pty-avatar">{initials(party.name)}</div>
          <h1 className="pty-name">{party.name}</h1>
          {party.phone && <p className="pty-phone">{party.phone}</p>}

          <div className="pty-net-label">Posisi bersih</div>
          <div className={`pty-net-amount pty-net-amount--${netClass}`}>
            Rp {FormatNumber(Math.round(Math.abs(net)))}
          </div>
          <p className="pty-net-direction">{direction}</p>

          {party.totals.progressPct > 0 && (
            <>
              <div className="pty-progress">
                <div
                  className="pty-progress__bar"
                  style={{ width: party.totals.progressPct + "%" }}
                />
              </div>
              <small className="pty-progress__label">
                {party.totals.progressPct}% pelunasan keseluruhan
              </small>
            </>
          )}

          <div className="pty-stats">
            <div>
              <div className="pty-stat__label">Piutang</div>
              <div className="pty-stat__value pty-stat__value--in">
                Rp {FormatNumber(Math.round(party.totals.totalPiutangOriginal))}
              </div>
              <div className="pty-stat__sub">total piutang</div>
            </div>
            <div>
              <div className="pty-stat__label">Pinjam</div>
              <div className="pty-stat__value pty-stat__value--out">
                Rp {FormatNumber(Math.round(party.totals.totalUtangOriginal))}
              </div>
              <div className="pty-stat__sub">total utang Anda</div>
            </div>
          </div>

          <div className="pty-actions">
            <button
              className="pty-btn pty-btn--primary"
              onClick={() => setShareModal(true)}
              disabled={!party.phone}
              title={!party.phone ? "Tambah no. HP dulu" : undefined}
            >
              <IoIosPaperPlane size="1.3em" /> Bagikan via WhatsApp
            </button>
            <button className="pty-btn pty-btn--ghost" onClick={handleCopy}>
              <IoIosLink size="1.2em" />
              {party.share && party.share.hasToken
                ? "Salin link bagikan"
                : "Buat link bagikan"}
            </button>
          </div>

          {party.share && party.share.hasToken && (
            <div className="pty-share-status">
              <IoIosCheckmark size="1.4em" />
              <div style={{ flex: 1 }}>
                Link aktif, berlaku sampai{" "}
                <strong>{FormatDate(party.share.expiresAt)}</strong>
                <br />
                <code>{party.share.url}</code>
              </div>
              <button
                onClick={() => setConfirmRevoke(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--expense)",
                  cursor: "pointer",
                }}
                aria-label="Cabut link"
              >
                <IoIosTrash />
              </button>
            </div>
          )}
        </div>

        <div className="pty-section-title">
          Riwayat · {party.timeline.length} transaksi
        </div>
        <div className="pty-timeline">
          {party.timeline.map((e, idx) => {
            const sign = e.amount >= 0 ? "in" : "out";
            return (
              <div
                className="pty-tl-event"
                key={idx}
                style={{ animationDelay: `${Math.min(idx * 0.04, 0.4)}s` }}
              >
                <span
                  className={`pty-tl-event__dot pty-tl-event__dot--${sign} ${
                    e.kind === "origin" ? "pty-tl-event__dot--filled" : ""
                  }`}
                />
                <div className="pty-tl-event__date">{FormatDate(e.at)}</div>
                <div className="pty-tl-event__row">
                  <span className="pty-tl-event__label">{e.label}</span>
                  <span
                    className={`pty-tl-event__amount pty-tl-event__amount--${sign}`}
                  >
                    {e.amount >= 0 ? "+" : "−"}Rp{" "}
                    {FormatNumber(Math.round(Math.abs(e.amount)))}
                  </span>
                </div>
                {e.note && <div className="pty-tl-event__note">"{e.note}"</div>}
                {e.kind === "payment" && e.runningOutstanding != null && (
                  <div className="pty-tl-event__sub">
                    Sisa: Rp{" "}
                    {FormatNumber(Math.round(e.runningOutstanding))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="pty-actions" style={{ marginTop: "1.5rem" }}>
          <button
            className="pty-btn pty-btn--ghost"
            onClick={() =>
              props.history.push(
                "/debts/add?partyId=" + encodeURIComponent(partyId)
              )
            }
          >
            <IoIosAdd /> Catat utang/piutang baru
          </button>
        </div>
      </div>

      <Modal
        show={shareModal}
        title="Bagikan ringkasan via WhatsApp"
        onClose={() => setShareModal(false)}
        onPositive={handleSendWa}
        onNegative={() => setShareModal(false)}
        textPositive={sending ? "Mengirim…" : "Kirim"}
        textNegative="Batal"
      >
        <p style={{ fontSize: "0.88rem" }}>
          {party.name} akan menerima link aman untuk melihat seluruh
          utang-piutang dengan Anda. Mereka cuma bisa lihat (read-only). Link
          berlaku 90 hari.
        </p>
        <small className="text-muted">
          {party.phone
            ? "Akan dikirim ke: " + party.phone
            : "Tambah nomor WA party dulu."}
        </small>
      </Modal>

      <Modal
        show={confirmRevoke}
        title="Cabut link bagikan?"
        onClose={() => setConfirmRevoke(false)}
        onPositive={handleRevoke}
        onNegative={() => setConfirmRevoke(false)}
        textPositive="Cabut"
        textNegative="Batal"
      >
        Link saat ini akan invalid. {party.name} tidak bisa lagi akses
        halaman ringkasan via link itu. Anda bisa buat link baru kapan saja.
      </Modal>
    </div>
  );
};

export default PartyDetail;
